/*
 * PROD-7828 — thin I/O layer for the Teacher Payments tabs (goserver /v2 routes, JWT +
 * requirePaymentGrant on every route). Params come from each tab's params.js and responses are shaped
 * by its adapters.js; this file only builds URLs and unwraps `data`.
 */
import { get_api, post_api } from '../../../redux/api_funcs';
import { get_api_url } from '../../../utils/urls';
import { envelope_data, extract_csv_payload } from './format';
import { build_query_string } from './overview/params';
import { disposition_file_name } from './shared/file_name';
import {
  csv_params as report_csv_params,
  data_params,
  issues_csv_params,
  status_params
} from './reports/requests';
import { summary_params } from './needs_attention/params';
import {
  run_params as month_run_params,
  runs_params as month_runs_params
} from './needs_attention/month_run/params';
import {
  list_params,
  sessions_lookup_params,
  teachers_lookup_params
} from './additional_payments/params';
import {
  file_params,
  run_params,
  runs_params,
  to_form_data,
  validate_fields
} from './additional_payments/upload/requests';
import {
  csv_params as close_versions_csv_params,
  list_params as close_versions_list_params,
  summary_params as close_versions_summary_params,
  teacher_month_params
} from './close_versions/requests';

const GROUP = '/v2/cms_user/teacher_payments';

const group_url = (path, params = {}) => {
  const query = build_query_string(params);
  return get_api_url(`${GROUP}${path}${query ? `?${query}` : ''}`);
};

const with_query = (path, params = {}) => group_url(`/overview${path}`, params);

export const fetch_months = () => get_api(with_query('/months'), true).then(envelope_data);

export const fetch_month_summary = (pay_month) =>
  get_api(with_query('/month_summary', { pay_month }), true).then(envelope_data);

// params: overview/params.js l1_params
export const fetch_l1 = (params) => get_api(with_query('/l1', params), true).then(envelope_data);

export const fetch_l2 = (params) => get_api(with_query('/l2', params), true).then(envelope_data);

// params: overview/params.js drilldown_params
export const fetch_drilldown = (params) =>
  get_api(with_query('/drilldown', params), true).then(envelope_data);

// Server-generated text/csv (D-12 tie-out export); the caller normalises via extract_csv_payload.
export const fetch_csv = (params) =>
  get_api(with_query('/csv', params), true).then((res) => res.data);

/*
 * R5b — Needs attention (issue log) + month close/reopen. Same group, same envelope; the writes are
 * POSTs whose bodies come from needs_attention/params.js and whose 4xx carry a stable error_code.
 */
export const fetch_needs_attention_summary = (pay_month) =>
  get_api(group_url('/needs_attention/summary', summary_params(pay_month)), true).then(
    envelope_data
  );

// params: needs_attention/params.js issues_params
export const fetch_needs_attention_issues = (params) =>
  get_api(group_url('/needs_attention/issues', params), true).then(envelope_data);

// params: needs_attention/params.js readiness_params
export const fetch_needs_attention_readiness = (params) =>
  get_api(group_url('/needs_attention/readiness', params), true).then(envelope_data);

// params: needs_attention/params.js daily_params
export const fetch_needs_attention_daily = (params) =>
  get_api(group_url('/needs_attention/daily', params), true).then(envelope_data);

export const post_issue_action = (payload) =>
  post_api(group_url('/needs_attention/issue_action'), payload, true).then(envelope_data);

// CD-94: a request that walks many rows, each waiting on its lock (penalties bulk, the upload apply),
// must not hit the 30s default and read as a dropped connection.
export const MONTH_BATCH_TIMEOUT_MS = 120000;

/*
 * R6 — Penalties. Same group and grant gate; query params and bodies come from penalties/requests.js.
 * A bulk request walks up to 200 rows one teacher-month at a time, each waiting on its month lock and
 * a recompute, so it gets the batch timeout too.
 */
const penalties_url = (path, params = {}) => group_url(`/penalties${path}`, params);

export const fetch_penalties_list = (params) =>
  get_api(penalties_url('/list', params), true).then(envelope_data);

export const fetch_penalties_summary = (params) =>
  get_api(penalties_url('/summary', params), true).then(envelope_data);

export const fetch_penalties_session = (params) =>
  get_api(penalties_url('/session', params), true).then(envelope_data);

export const fetch_penalties_free_pass = (params) =>
  get_api(penalties_url('/free_pass', params), true).then(envelope_data);

export const post_penalty_correct = (payload) =>
  post_api(penalties_url('/correct'), payload, true).then(envelope_data);

export const post_penalty_waive = (payload) =>
  post_api(penalties_url('/waive'), payload, true).then(envelope_data);

export const post_penalty_revert = (payload) =>
  post_api(penalties_url('/revert'), payload, true).then(envelope_data);

export const post_penalty_remove = (payload) =>
  post_api(penalties_url('/remove'), payload, true).then(envelope_data);

export const post_penalty_add = (payload) =>
  post_api(penalties_url('/add'), payload, true).then(envelope_data);

export const post_penalty_free_pass = (payload) =>
  post_api(penalties_url('/free_pass'), payload, true).then(envelope_data);

export const post_penalties_bulk = (payload) =>
  post_api(penalties_url('/bulk'), payload, true, 'none', MONTH_BATCH_TIMEOUT_MS).then(
    envelope_data
  );

/*
 * R8 — Additional payments (manual entries). Same JWT group and the same
 * requirePaymentGrant on every route, reads included; params come from additional_payments/params.js.
 */
const additional_url = (path, params = {}) => group_url(`/additional${path}`, params);

export const fetch_additional_categories = () =>
  get_api(additional_url('/categories'), true).then(envelope_data);

export const fetch_additional_list = (filters, limit, offset) =>
  get_api(additional_url('/list', list_params(filters, limit, offset)), true).then(envelope_data);

export const fetch_additional_teachers = (q) =>
  get_api(additional_url('/teachers_lookup', teachers_lookup_params(q)), true).then(envelope_data);

export const fetch_additional_sessions = (params) =>
  get_api(additional_url('/sessions_lookup', sessions_lookup_params(params)), true).then(
    envelope_data
  );

export const create_additional = (payload) =>
  post_api(additional_url('/create'), payload, true).then(envelope_data);

export const update_additional = (payload) =>
  post_api(additional_url('/update'), payload, true).then(envelope_data);

export const remove_additional = (payload) =>
  post_api(additional_url('/remove'), payload, true).then(envelope_data);

/*
 * R10 — Reports and the to-be-reviewed CSV (CD-36); the audit trail and the rules page keep their calls
 * in audit/api.js and rules/api.js. Every month is 'YYYY-MM' except /recalc's 'YYYY-MM-01'.
 */

// A server text/csv plus the file name its Content-Disposition carries, when the browser may read it
// ('' otherwise: the header is not CORS-exposed, so the caller names the file the same way the server does).
const csv_download = (res) => ({
  csv: extract_csv_payload(res && res.data),
  file_name: disposition_file_name(res && res.headers)
});

export const fetch_report_status = (pay_month) =>
  get_api(group_url('/reports/status', status_params(pay_month)), true).then(envelope_data);

export const fetch_report_data = (report, pay_month, preview) =>
  get_api(group_url('/reports/data', data_params(report, pay_month, preview)), true).then(
    envelope_data
  );

export const fetch_report_csv = (report, pay_month) =>
  get_api(group_url('/reports/csv', report_csv_params(report, pay_month)), true).then(csv_download);

// filters: the issue list's own (pay_month, tier, issue_code, teacher_uuid, status, sort, dir).
export const fetch_issues_csv = (filters) =>
  get_api(group_url('/needs_attention/issues/csv', issues_csv_params(filters)), true).then(
    csv_download
  );

/*
 * R9 — Additional payments by CSV upload, plus the grant-gated product /recalc (CD-33). Bodies and
 * query params come from additional_payments/upload/requests.js; every month is 'YYYY-MM-01'.
 */
const upload_url = (path, params = {}) => additional_url(`/upload${path}`, params);

// Server-generated text/csv header row; returned as text, not an envelope.
export const fetch_upload_template = () =>
  get_api(upload_url('/template'), true).then((res) => res.data);

// Parses, stores the file in S3 and persists the preview; the 120 s formdata ceiling applies.
export const validate_upload = (file, pay_month) =>
  post_api(
    upload_url('/validate'),
    to_form_data(validate_fields(file, pay_month)),
    true,
    'formdata'
  ).then(envelope_data);

export const fetch_upload_run = (run_uuid) =>
  get_api(upload_url('/run', run_params(run_uuid)), true).then(envelope_data);

export const fetch_upload_runs = (pay_month, limit, offset) =>
  get_api(upload_url('/runs', runs_params(pay_month, limit, offset)), true).then(envelope_data);

export const fetch_upload_file_link = (run_uuid) =>
  get_api(upload_url('/file', file_params(run_uuid)), true).then(envelope_data);

// One transaction for up to 1,000 rows plus a 20 s recompute budget: wider than the 30 s default.
export const apply_upload = (payload) =>
  post_api(upload_url('/apply'), payload, true, 'none', MONTH_BATCH_TIMEOUT_MS).then(envelope_data);

export const discard_upload = (payload) =>
  post_api(upload_url('/discard'), payload, true).then(envelope_data);

// The grant-gated recompute (CD-33): R9's post-apply remainder and R10's "Recalculate now" both use it.
export const post_recalc = (payload) =>
  post_api(group_url('/recalc'), payload, true).then(envelope_data);

/*
 * D110 — month close/reopen runs. A create answers in seconds and the server closes the teachers one
 * by one, so these keep the default timeout. Bodies/params come from needs_attention/month_run/params.js.
 */
export const post_month_run = (payload) =>
  post_api(group_url('/month/runs'), payload, true).then(envelope_data);

export const fetch_month_run = (run_uuid, bucket, limit, offset) =>
  get_api(group_url('/month/run', month_run_params(run_uuid, bucket, limit, offset)), true).then(
    envelope_data
  );

export const fetch_month_runs = (pay_month, limit, offset) =>
  get_api(group_url('/month/runs', month_runs_params(pay_month, limit, offset)), true).then(
    envelope_data
  );

export const post_month_run_cancel = (payload) =>
  post_api(group_url('/month/run/cancel'), payload, true).then(envelope_data);

export const post_month_run_resume = (payload) =>
  post_api(group_url('/month/run/resume'), payload, true).then(envelope_data);

/*
 * D111 — close versions (reads only). `state` is close_versions/params.js view state and `mode` the
 * effective mode; every month is 'YYYY-MM'.
 */
const close_versions_url = (path, params) => group_url(`/close_versions${path}`, params);

export const fetch_close_versions_summary = (pay_month) =>
  get_api(close_versions_url('/summary', close_versions_summary_params(pay_month)), true).then(
    envelope_data
  );

export const fetch_close_versions_list = (pay_month, state, mode) =>
  get_api(
    close_versions_url('/list', close_versions_list_params(pay_month, state, mode)),
    true
  ).then(envelope_data);

export const fetch_close_versions_teacher_month = (pay_month, teacher_uuid) =>
  get_api(
    close_versions_url('/teacher_month', teacher_month_params(pay_month, teacher_uuid)),
    true
  ).then(envelope_data);

export const fetch_close_versions_csv = (pay_month, state, mode) =>
  get_api(close_versions_url('/csv', close_versions_csv_params(pay_month, state, mode)), true).then(
    csv_download
  );
